import { useState, useEffect, useRef } from "react";
import logo from './logo.png';
import { ArrowUpRight, Check, ChevronDown } from "lucide-react";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>
      {children}
    </div>
  );
}

function Nav({ scrolled }) {
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      height: 64, display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 48px",
      background: scrolled ? "rgba(255,255,255,0.94)" : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? "1px solid #f0f0f0" : "none",
      transition: "all 0.35s ease"
    }}>
      <a href="/home" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <img src={logo} style={{ width: 36, height: 36 }} alt="Binos" />
        <span style={{ fontWeight: 900, fontSize: 19, color: scrolled ? "#0f0f0f" : "#fff", letterSpacing: "-0.5px", transition: "color 0.35s" }}>Binos</span>
      </a>
      <div style={{ display: "flex", alignItems: "center", gap: 36 }}>
        {["Product", "Pricing", "About"].map(l => (
          <a key={l} href={`/${l.toLowerCase()}`} style={{ fontSize: 14, fontWeight: l === "Product" ? 700 : 500, color: scrolled ? (l === "Product" ? "#0f0f0f" : "#555") : "rgba(255,255,255,0.7)", textDecoration: "none", transition: "color 0.2s" }}
            onMouseEnter={e => e.target.style.color = scrolled ? "#0f0f0f" : "#fff"}
            onMouseLeave={e => e.target.style.color = scrolled ? (l === "Product" ? "#0f0f0f" : "#555") : "rgba(255,255,255,0.7)"}>{l}</a>
        ))}
        <a href="/login" style={{ fontSize: 14, fontWeight: 600, color: scrolled ? "#0f0f0f" : "rgba(255,255,255,0.7)", textDecoration: "none" }}>Log in</a>
        <a href="/waitlist" style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "9px 18px", background: "#dc2626", color: "#fff",
          borderRadius: 9, fontSize: 13, fontWeight: 700, textDecoration: "none"
        }}>
          Get early access <ArrowUpRight style={{ width: 13, height: 13 }} />
        </a>
      </div>
    </nav>
  );
}

export default function ProductPage() {
  const [scrolled, setScrolled] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 80);
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const S = (delay) => ({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`
  });

  const features = [
    {
      tag: "Pitch Intelligence",
      headline: "Read any pitch deck in 60 seconds.",
      sub: "Most VCs spend 6-8 hours a week just reading decks. Binos reads them for you — Canva, image-heavy PDFs, scanned documents — and pulls out every signal that matters.",
      points: [
        "Works on every PDF format including Canva and image-heavy decks",
        "Extracts 9 investment signals — problem, solution, market size, traction, team, ask and more",
        "Bulk upload up to 50 decks at once",
        "50 deck daily limit per user — resets at midnight",
        "Star and filter your best finds instantly",
      ],
      mockup: "pitch"
    },
    {
      tag: "Thesis Matcher",
      headline: "Find deals that match how you think.",
      sub: "Every VC has a thesis. Most tools ignore it. Binos reads yours — in plain English — and scores every company in your deal flow against your exact investment criteria.",
      points: [
        "Paste your thesis in plain English — no templates needed",
        "AI scores each company 0–100 against your criteria",
        "Full reasoning for every match — know exactly why",
        "Green, yellow, red scoring — instantly see who fits",
        "Powered by Crunchbase data via your own API key",
      ],
      mockup: "thesis"
    },
    {
      tag: "Portfolio Health",
      headline: "Never miss a portfolio red flag.",
      sub: "Your portfolio companies are counting on you to notice before things go wrong. Binos tracks runway, founder updates, and health signals across every company — so you always know who needs your attention.",
      points: [
        "Real-time runway tracking with color-coded alerts",
        "Founder CRM with contact details and update history",
        "7 tabs of company data — Overview, Metrics, Founders, Funding, Notes, Tasks, Documents",
        "Add companies instantly with full detail modal",
        "Search, filter and sort by sector, stage, health or runway",
      ],
      mockup: "portfolio"
    },
  ];

  const PitchMockup = () => (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #efefef", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.08)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: "#0f0f0f", margin: 0 }}>Pitch Summariser</p>
        <div style={{ padding: "5px 12px", background: "#dc2626", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#fff" }}>+ Upload Decks</div>
      </div>
      <div style={{ padding: "16px" }}>
        {[
          { name: "NexaFlow_Deck.pdf", status: "Done", score: "94" },
          { name: "MediGen_Series_A.pdf", status: "Done", score: "87" },
          { name: "EcoGrid_Pitch.pdf", status: "Processing", score: "--" },
        ].map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: i === 0 ? "#fafafa" : "#fff", borderRadius: 10, marginBottom: 8, border: "1px solid #f5f5f5" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, background: "#fee2e2", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#dc2626" }}>PDF</div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#0f0f0f", margin: 0 }}>{d.name}</p>
                <p style={{ fontSize: 10, color: "#999", margin: 0, fontWeight: 500 }}>{d.status === "Done" ? "9 signals extracted" : "Analysing..."}</p>
              </div>
            </div>
            <div style={{ padding: "4px 10px", borderRadius: 100, background: d.status === "Done" ? "#dcfce7" : "#fef9c3", color: d.status === "Done" ? "#16a34a" : "#ca8a04", fontSize: 10, fontWeight: 800 }}>
              {d.status === "Done" ? `${d.score}% match` : "Processing"}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: "16px", background: "#fafafa", borderRadius: 12, border: "1px solid #f0f0f0" }}>
          <p style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 10px" }}>NexaFlow — AI Summary</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { l: "Problem", v: "B2B procurement is broken" },
              { l: "Market", v: "$4.2B TAM, growing 28% YoY" },
              { l: "Traction", v: "$1.2M ARR, 140% NRR" },
              { l: "Ask", v: "$3M Seed" },
            ].map((s, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 8, padding: "8px 10px", border: "1px solid #f0f0f0" }}>
                <p style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", margin: "0 0 3px" }}>{s.l}</p>
                <p style={{ fontSize: 11, fontWeight: 600, color: "#0f0f0f", margin: 0 }}>{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const ThesisMockup = () => (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #efefef", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.08)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5" }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: "#0f0f0f", margin: "0 0 10px" }}>Thesis Matcher</p>
        <div style={{ padding: "10px 14px", background: "#fafafa", borderRadius: 10, border: "1px solid #f0f0f0", fontSize: 11, color: "#777", lineHeight: 1.6 }}>
          "We invest in B2B SaaS at Seed or Series A in India. Technical founding team, ARR above ₹2Cr, NRR above 110%..."
        </div>
      </div>
      <div style={{ padding: "16px" }}>
        {[
          { name: "NexaFlow", score: 94, color: "#16a34a", checks: [true, true, true, true] },
          { name: "MediGen", score: 71, color: "#d97706", checks: [true, true, false, true] },
          { name: "EcoGrid", score: 38, color: "#dc2626", checks: [false, true, false, false] },
        ].map((c, i) => (
          <div key={i} style={{ marginBottom: 12, padding: "14px 16px", background: "#fafafa", borderRadius: 12, border: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 800, color: "#0f0f0f", margin: 0 }}>{c.name}</p>
              <span style={{ fontSize: 12, fontWeight: 800, color: c.color }}>{c.score}% match</span>
            </div>
            <div style={{ height: 4, background: "#f0f0f0", borderRadius: 100, overflow: "hidden", marginBottom: 10 }}>
              <div style={{ height: "100%", width: `${c.score}%`, background: c.color, borderRadius: 100 }} />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {["B2B SaaS", "India", "ARR ₹2Cr+", "NRR 110%+"].map((label, j) => (
                <div key={j} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", background: c.checks[j] ? "#dcfce7" : "#fee2e2", borderRadius: 100 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: c.checks[j] ? "#16a34a" : "#dc2626" }}>{c.checks[j] ? "✓" : "✗"} {label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const PortfolioMockup = () => (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #efefef", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.08)" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: "#0f0f0f", margin: 0 }}>Fund Health</p>
        <div style={{ padding: "5px 12px", background: "#dc2626", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#fff" }}>+ Add Company</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "#f5f5f5", borderBottom: "1px solid #f5f5f5" }}>
        {[{ l: "Portfolio", v: "3" }, { l: "Avg Runway", v: "14m" }, { l: "Healthy", v: "2" }, { l: "At Risk", v: "1" }].map((m, i) => (
          <div key={i} style={{ background: "#fff", padding: "14px 16px", textAlign: "center" }}>
            <p style={{ fontSize: 9, color: "#999", fontWeight: 700, textTransform: "uppercase", margin: "0 0 4px" }}>{m.l}</p>
            <p style={{ fontSize: 20, fontWeight: 900, color: "#0f0f0f", margin: 0, letterSpacing: "-1px" }}>{m.v}</p>
          </div>
        ))}
      </div>
      <div style={{ padding: "16px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {[
          { name: "NexaFlow", sector: "B2B SaaS", health: "Healthy", runway: "18m", color: "#16a34a" },
          { name: "MediGen", sector: "HealthTech", health: "Watch", runway: "9m", color: "#d97706" },
          { name: "EcoGrid", sector: "CleanTech", health: "At Risk", runway: "4m", color: "#dc2626" },
        ].map((c, i) => (
          <div key={i} style={{ padding: "14px", border: "1px solid #f0f0f0", borderRadius: 12, background: "#fafafa" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, background: "#f0f0f0", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#555" }}>{c.name.slice(0, 2)}</div>
              <span style={{ fontSize: 9, fontWeight: 800, color: c.color, background: c.color + "15", padding: "2px 7px", borderRadius: 100 }}>{c.health}</span>
            </div>
            <p style={{ fontSize: 12, fontWeight: 800, color: "#0f0f0f", margin: "0 0 2px" }}>{c.name}</p>
            <p style={{ fontSize: 10, color: "#999", margin: "0 0 8px", fontWeight: 500 }}>{c.sector}</p>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 10, color: "#999" }}>Runway</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: c.color }}>{c.runway}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const mockups = [<PitchMockup />, <ThesisMockup />, <PortfolioMockup />];

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#fff", color: "#0f0f0f", overflowX: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
      <Nav scrolled={scrolled} />

      {/* HERO — dark */}
      <section style={{ background: "#0f0f0f", minHeight: "80vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "120px 48px 80px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "48px 48px", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 800, height: 400, background: "radial-gradient(ellipse, rgba(220,38,38,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 760 }}>
          <div style={S(0.1)}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", border: "1px solid rgba(220,38,38,0.4)", borderRadius: 100, marginBottom: 36 }}>
              <div style={{ width: 6, height: 6, background: "#dc2626", borderRadius: "50%" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#dc2626", letterSpacing: "0.05em" }}>The Product</span>
            </div>
          </div>
          <div style={S(0.2)}>
            <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(44px, 7vw, 80px)", fontWeight: 400, color: "#fff", lineHeight: 1.0, letterSpacing: "-2.5px", margin: "0 0 24px" }}>
              Everything your fund needs.<br />
              <span style={{ fontStyle: "italic", color: "#dc2626" }}>Nothing it doesn't.</span>
            </h1>
          </div>
          <div style={S(0.35)}>
            <p style={{ fontSize: 18, color: "rgba(255,255,255,0.5)", lineHeight: 1.7, maxWidth: 500, margin: "0 auto 48px" }}>
              Three core features built around the way VC analysts actually spend their time. No bloat. No fluff.
            </p>
          </div>
          <div style={{ ...S(0.5), display: "flex", justifyContent: "center", gap: 12 }}>
            {features.map((f, i) => (
              <button key={i} onClick={() => setActiveFeature(i)} style={{
                padding: "8px 20px", borderRadius: 100, fontSize: 13, fontWeight: 700,
                border: activeFeature === i ? "none" : "1px solid rgba(255,255,255,0.15)",
                background: activeFeature === i ? "#dc2626" : "transparent",
                color: activeFeature === i ? "#fff" : "rgba(255,255,255,0.5)",
                cursor: "pointer", transition: "all 0.2s"
              }}>{f.tag}</button>
            ))}
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)", opacity: 0.3 }}>
          <ChevronDown style={{ width: 18, height: 18, color: "#fff" }} />
        </div>
      </section>

      {/* FEATURE DEEP DIVES — light */}
      {features.map((f, i) => (
        <section key={i} style={{ padding: "100px 48px", background: i % 2 === 0 ? "#fff" : "#fafafa", borderTop: "1px solid #f0f0f0" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
            <FadeIn delay={0}>
              <div style={{ order: i % 2 === 0 ? 0 : 1 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 16 }}>{f.tag}</p>
                <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(32px, 3.5vw, 48px)", fontWeight: 400, letterSpacing: "-1.5px", margin: "0 0 20px", color: "#0f0f0f", lineHeight: 1.1 }}>{f.headline}</h2>
                <p style={{ fontSize: 16, color: "#666", lineHeight: 1.8, margin: "0 0 32px" }}>{f.sub}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {f.points.map((p, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#dc262610", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        <Check style={{ width: 11, height: 11, color: "#dc2626" }} />
                      </div>
                      <p style={{ fontSize: 14, fontWeight: 500, color: "#555", margin: 0, lineHeight: 1.6 }}>{p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div style={{ order: i % 2 === 0 ? 1 : 0 }}>
                {mockups[i]}
              </div>
            </FadeIn>
          </div>
        </section>
      ))}

      {/* MORE FEATURES — dark */}
      <section style={{ background: "#0f0f0f", padding: "100px 48px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 16, textAlign: "center" }}>Also included</p>
            <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 400, color: "#fff", letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 64px", lineHeight: 1.1 }}>
              The full toolkit for serious funds.
            </h2>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {[
              { tag: "VC Finance Engine", title: "Investment-grade financial analysis.", body: "Burn Multiple, Rule of 40, LTV:CAC, Magic Number and more — calculated instantly with a clear Invest / Watch / Pass verdict.", bullets: ["10+ VC-specific metrics", "INR, USD, EUR and more", "Radar and bar chart views"] },
              { tag: "Market Research", title: "Live data from Crunchbase.", body: "Search any company or sector using your own Crunchbase API key. Full funding history, investor list, team size and more.", bullets: ["BYOK — bring your own key", "Live company data", "Tracxn and Dealroom coming soon"] },
              { tag: "Reporting", title: "Portfolio narrative at a glance.", body: "Track founder update submissions, LP narrative drafting status, and tax filing progress across your entire portfolio.", bullets: ["Update submission tracking", "LP narrative drafting", "Q1 collection trigger"] },
            ].map((f, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div style={{ padding: "36px 32px", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 20, transition: "all 0.3s", cursor: "default" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(220,38,38,0.4)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "transparent"; }}>
                  <p style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", margin: "0 0 14px" }}>{f.tag}</p>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: "0 0 12px", letterSpacing: "-0.3px", lineHeight: 1.25 }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", lineHeight: 1.75, margin: "0 0 20px" }}>{f.body}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {f.bullets.map((b, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Check style={{ width: 11, height: 11, color: "#dc2626", flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "100px 48px", background: "#fff", textAlign: "center" }}>
        <FadeIn>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 20 }}>Ready to see it live?</p>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 400, letterSpacing: "-2px", margin: "0 0 20px", color: "#0f0f0f", lineHeight: 1.1 }}>
            Your entire fund.<br />
            <span style={{ fontStyle: "italic", color: "#dc2626" }}>One screen.</span>
          </h2>
          <p style={{ fontSize: 16, color: "#888", margin: "0 auto 44px", maxWidth: 440, lineHeight: 1.7 }}>Early access is open for Indian VC firms. First 10 firms get locked-in pricing forever.</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
            <a href="/waitlist" style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 28px", background: "#0f0f0f", color: "#fff", borderRadius: 11, fontSize: 15, fontWeight: 700, textDecoration: "none", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#0f0f0f"; e.currentTarget.style.transform = "translateY(0)"; }}>
              Get early access <ArrowUpRight style={{ width: 16, height: 16 }} />
            </a>
            <a href="/dashboard" style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 28px", background: "#fff", color: "#0f0f0f", borderRadius: 11, fontSize: 15, fontWeight: 600, textDecoration: "none", border: "1px solid #e5e5e5", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#0f0f0f"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#e5e5e5"; e.currentTarget.style.transform = "translateY(0)"; }}>
              Try the dashboard →
            </a>
          </div>
        </FadeIn>
      </section>

      {/* FOOTER */}
      <footer style={{ background: "#0a0a0a", padding: "40px 48px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logo} style={{ width: 30, height: 30 }} alt="Binos" />
            <span style={{ fontWeight: 900, fontSize: 16, color: "#fff", letterSpacing: "-0.5px" }}>Binos</span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", marginLeft: 8 }}>The VC Operating System</span>
          </div>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0 }}>© 2025 Binos. Built in Mumbai.</p>
          <div style={{ display: "flex", gap: 24 }}>
            {["About", "Pricing", "Privacy", "Terms"].map(l => (
              <a key={l} href={`/${l.toLowerCase()}`} style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", textDecoration: "none", fontWeight: 500, transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "rgba(255,255,255,0.7)"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.25)"}>{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
